import type {Content} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import type {SliceComponentProps} from '@prismicio/react'
import {useLinkResolver} from 'prismicio'
import {getButtonsData, getResolvedLink} from 'src/common/helpers'
import type {Context} from 'src/common/types'
import {BlobSection} from './BlobSection'
import {ButtonBlobSection} from './ButtonBlobSection'
import {DefaultHubspotBlobSection} from './DefaultHubspotBlobSection'
import {DefaultLeadCaptureBlobSection} from './DefaultLeadCaptureBlobSection'
import {InputBlobSection} from './InputBlobSection'
import {MultiStepBlobSection} from './MultiStepBlobSection'
import {WithInputHubspotBlobSection} from './WithInputHubspotBlobSection'

export type BlobSectionProps = SliceComponentProps<Content.BlobSectionSlice>

const BlobSectionSlice = ({
  slice,
  context: {pageUid, buttonsData},
}: BlobSectionProps & {context: Context}): JSX.Element | null => {
  const {linkResolver} = useLinkResolver()

  switch (slice.variation) {
    case 'default': {
      const {primaryButtonData, secondaryButtonData} = getButtonsData({
        buttonsData,
        primaryButton: slice.primary.primary_button,
        secondaryButton: slice.primary.secondary_button,
      })

      return (
        <BlobSection
          eyebrow={isFilled.keyText(slice.primary.eyebrow) ? slice.primary.eyebrow : ''}
          title={slice.primary.title}
          description={isFilled.keyText(slice.primary.description) ? slice.primary.description : ''}
          descriptionWidth={slice.primary.description_width}
          primaryButtonData={primaryButtonData}
          secondaryButtonData={secondaryButtonData}
          disclaimer={isFilled.keyText(slice.primary.disclaimer) ? slice.primary.disclaimer : ''}
          videoUrl={isFilled.linkToMedia(slice.primary.video) ? slice.primary.video.url : null}
          videoPosterUrl={isFilled.image(slice.primary.video_poster) ? slice.primary.video_poster.url : null}
          pageUid={pageUid}
          paddingTop={slice.primary.padding_top}
          paddingBottom={slice.primary.padding_bottom}
        />
      )
    }

    case 'defaultHubspot': {
      const inputField = slice.primary.input_field?.[0]

      return (
        <DefaultHubspotBlobSection
          eyebrow={isFilled.keyText(slice.primary.eyebrow) ? slice.primary.eyebrow : ''}
          title={slice.primary.title}
          description={isFilled.keyText(slice.primary.description) ? slice.primary.description : ''}
          videoUrl={isFilled.linkToMedia(slice.primary.video) ? slice.primary.video.url : null}
          videoPosterUrl={isFilled.image(slice.primary.video_poster) ? slice.primary.video_poster.url : null}
          placeholder={isFilled.keyText(inputField?.placeholder) ? inputField.placeholder : ''}
          submitButtonLabel={isFilled.keyText(inputField?.submit_button_label) ? inputField.submit_button_label : ''}
          successMessage={isFilled.keyText(inputField?.success_message) ? inputField.success_message : ''}
          genericError={isFilled.keyText(inputField?.generic_error) ? inputField.generic_error : ''}
          checkboxLabel={inputField?.checkbox_label ?? []}
          disclaimer={isFilled.keyText(slice.primary.disclaimer) ? slice.primary.disclaimer : ''}
          hubspotPortalId={isFilled.keyText(slice.primary.hubspot_portal_id) ? slice.primary.hubspot_portal_id : ''}
          hubspotFormId={isFilled.keyText(slice.primary.hubspot_form_id) ? slice.primary.hubspot_form_id : ''}
          pageUid={pageUid}
          paddingTop={slice.primary.padding_top}
          paddingBottom={slice.primary.padding_bottom}
        />
      )
    }

    case 'defaultLeadCapture': {
      const inputField = slice.primary.input_field?.[0]
      const resolvedSubmitLink = getResolvedLink({link: inputField?.submit_link, buttonsData})

      return (
        <DefaultLeadCaptureBlobSection
          eyebrow={isFilled.keyText(slice.primary.eyebrow) ? slice.primary.eyebrow : ''}
          title={slice.primary.title}
          description={isFilled.keyText(slice.primary.description) ? slice.primary.description : ''}
          videoUrl={isFilled.linkToMedia(slice.primary.video) ? slice.primary.video.url : null}
          videoPosterUrl={isFilled.image(slice.primary.video_poster) ? slice.primary.video_poster.url : null}
          placeholder={isFilled.keyText(inputField?.placeholder) ? inputField.placeholder : ''}
          submitButtonLabel={isFilled.keyText(inputField?.submit_button_label) ? inputField.submit_button_label : ''}
          submitLink={isFilled.link(resolvedSubmitLink) ? linkResolver(resolvedSubmitLink, 'blob-section') : null}
          genericError={isFilled.keyText(inputField?.generic_error) ? inputField.generic_error : ''}
          disclaimer={isFilled.keyText(slice.primary.disclaimer) ? slice.primary.disclaimer : ''}
          pageUid={pageUid}
          paddingTop={slice.primary.padding_top}
          paddingBottom={slice.primary.padding_bottom}
        />
      )
    }

    case 'multiStep':
      return (
        <MultiStepBlobSection
          eyebrow={isFilled.keyText(slice.primary.eyebrow) ? slice.primary.eyebrow : ''}
          title={isFilled.keyText(slice.primary.title) ? slice.primary.title : ''}
          description={isFilled.keyText(slice.primary.description) ? slice.primary.description : ''}
          descriptionWidth={slice.primary.description_width}
          steps={slice.items.map((item) => ({
            title: isFilled.keyText(item.step_title) ? item.step_title : '',
            description: isFilled.keyText(item.step_description) ? item.step_description : '',
            image: item.step_image,
          }))}
          backgroundMode={slice.primary.background_mode === 'Solid green' ? 'Solid green' : 'Blobs'}
          paddingTop={slice.primary.padding_top}
          paddingBottom={slice.primary.padding_bottom}
        />
      )

    case 'withInput': {
      const inputField = slice.primary.input_field?.[0]
      const resolvedSubmitLink = getResolvedLink({link: inputField?.submit_link, buttonsData})

      return (
        <InputBlobSection
          title={isFilled.keyText(slice.primary.title) ? slice.primary.title : ''}
          description={isFilled.keyText(slice.primary.description) ? slice.primary.description : ''}
          descriptionWidth={slice.primary.description_width}
          placeholder={isFilled.keyText(inputField?.placeholder) ? inputField.placeholder : ''}
          submitButtonLabel={isFilled.keyText(inputField?.submit_button_label) ? inputField.submit_button_label : ''}
          submitLink={isFilled.link(resolvedSubmitLink) ? linkResolver(resolvedSubmitLink, 'blob-section') : null}
          genericError={isFilled.keyText(inputField?.generic_error) ? inputField.generic_error : ''}
          disclaimer={isFilled.keyText(slice.primary.disclaimer) ? slice.primary.disclaimer : ''}
          pageUid={pageUid}
          paddingTop={slice.primary.padding_top}
          paddingBottom={slice.primary.padding_bottom}
        />
      )
    }

    case 'withInputHubspot': {
      const inputField = slice.primary.input_field?.[0]

      return (
        <WithInputHubspotBlobSection
          title={isFilled.keyText(slice.primary.title) ? slice.primary.title : ''}
          description={isFilled.keyText(slice.primary.description) ? slice.primary.description : ''}
          descriptionWidth={slice.primary.description_width}
          placeholder={isFilled.keyText(inputField?.placeholder) ? inputField.placeholder : ''}
          submitButtonLabel={isFilled.keyText(inputField?.submit_button_label) ? inputField.submit_button_label : ''}
          successMessage={isFilled.keyText(inputField?.success_message) ? inputField.success_message : ''}
          genericError={isFilled.keyText(inputField?.generic_error) ? inputField.generic_error : ''}
          checkboxLabel={inputField?.checkbox_label ?? []}
          disclaimer={isFilled.keyText(slice.primary.disclaimer) ? slice.primary.disclaimer : ''}
          hubspotPortalId={isFilled.keyText(slice.primary.hubspot_portal_id) ? slice.primary.hubspot_portal_id : ''}
          hubspotFormId={isFilled.keyText(slice.primary.hubspot_form_id) ? slice.primary.hubspot_form_id : ''}
          pageUid={pageUid}
          paddingTop={slice.primary.padding_top}
          paddingBottom={slice.primary.padding_bottom}
        />
      )
    }

    case 'withButton': {
      const resolvedLink = getResolvedLink({link: slice.primary.primary_button, buttonsData})

      const buttonHref = isFilled.link(resolvedLink) ? linkResolver(resolvedLink, 'blob-section') : undefined

      const buttonTarget =
        isFilled.link(resolvedLink) && resolvedLink.link_type === 'Web' && resolvedLink.target
          ? resolvedLink.target
          : undefined

      return (
        <ButtonBlobSection
          title={isFilled.keyText(slice.primary.title) ? slice.primary.title : ''}
          description={isFilled.keyText(slice.primary.description) ? slice.primary.description : ''}
          descriptionWidth={slice.primary.description_width}
          disclaimer={isFilled.keyText(slice.primary.disclaimer) ? slice.primary.disclaimer : ''}
          buttonLabel={isFilled.keyText(slice.primary.button_text) ? slice.primary.button_text : ''}
          buttonHref={buttonHref}
          buttonTarget={buttonTarget}
          pageUid={pageUid}
          paddingTop={slice.primary.padding_top}
          paddingBottom={slice.primary.padding_bottom}
        />
      )
    }

    default:
      return null
  }
}

export default BlobSectionSlice
